import { useCallback, useEffect, useMemo, useState } from "react";

import { ReactIsland } from "@/shared/reactIsland";
import {
	createDataTableColumnHelper,
	DataTable,
	useDataTable,
} from "@/primitives/DataTable";
import { DropdownMenu } from "@/primitives/DropdownMenu";
import { ContextMenu } from "@/primitives/ContextMenu";
import { AlertDialog } from "@/primitives/AlertDialog";
import { Modal } from "@/primitives/Modal";
import { Button } from "@/primitives/Button";
import { FA6Icon } from "@/primitives/FA6Icon";
import { api } from "@/shared/api";
import { useDialogState } from "@/shared/useDialogState";
import type { OAuthClient } from "@/shared/timr";
import { formatDateTime, parseInstant, Temporal } from "@/shared/utils/datefunctions";
import { grantTypeLabel } from "./oAuthClientSchema";
import { OAuthClientCreateDialog } from "./OAuthClientCreateDialog";
import {
	OAuthClientDetailsDialog,
	type OAuthClientDetailsMode,
	type OAuthSettings,
} from "./OAuthClientDetailsDialog";
import styles from "./apiCredentialsPanel.module.css";

export interface ApiCredentialsPanelProps {
	/** The card stays visible without the API feature so existing credentials can still be shown and deleted. */
	apiFeatureAllowed: boolean;
	oAuthSettings: OAuthSettings;
}

interface OAuthClientDetailsState {
	oAuthClient: OAuthClient;
	mode: OAuthClientDetailsMode;
	/** The mode the dialog was opened in — there is no view step to return to when that was "edit". */
	openedIn: OAuthClientDetailsMode;
}

interface MenuEntry {
	label: string;
	action: () => void;
}

const REST_API_ENDPOINT = "api.timr.com/v1";

const EMPTY: OAuthClient[] = [];

const columnHelper = createDataTableColumnHelper<OAuthClient>();

export function ApiCredentialsPanel({ apiFeatureAllowed, oAuthSettings }: ApiCredentialsPanelProps) {
	const [oAuthClients, setOAuthClients] = useState<OAuthClient[] | null>(null);
	const [loading, setLoading] = useState(false);
	const createDialog = useDialogState();
	const detailsDialog = useDialogState<OAuthClientDetailsState>();
	const [deleting, setDeleting] = useState<OAuthClient | null>(null);
	const [featureAlertOpen, setFeatureAlertOpen] = useState(false);
	const [contextRow, setContextRow] = useState<OAuthClient | null>(null);

	const reload = useCallback(() => {
		setLoading(true);
		api("/admin/getOAuthClients.json")
			.get()
			.done((response) => setOAuthClients(response.data))
			.always(() => setLoading(false));
	}, []);

	useEffect(() => {
		reload();
	}, [reload]);

	const { show: showDetails } = detailsDialog;
	const openDetails = useCallback((oAuthClient: OAuthClient, mode: OAuthClientDetailsMode) => {
		showDetails({ oAuthClient, mode, openedIn: mode });
	}, [showDetails]);

	const menuEntries = useCallback(
		(oAuthClient: OAuthClient): MenuEntry[] => [
			{
				label: messages["oAuthClient.context.show"],
				action: () => openDetails(oAuthClient, "view"),
			},
			...(apiFeatureAllowed
				? [{
					label: messages["oAuthClient.context.edit"],
					action: () => openDetails(oAuthClient, "edit"),
				}]
				: []),
			{
				label: messages["oAuthClient.context.delete"],
				action: () => setDeleting(oAuthClient),
			},
		],
		[apiFeatureAllowed, openDetails],
	);

	const columns = useMemo(
		() =>
			columnHelper.columns([
				columnHelper.display({
					id: "actions",
					size: 44,
					cell: ({ row }) => (
						<DropdownMenu.Root>
							<DropdownMenu.Trigger
								className={styles.rowActionsTrigger}
								aria-label={messages["oAuthClient.title"]}
								onDoubleClick={(event) => event.stopPropagation()}
							>
								<FA6Icon icon="bars" variant="regular" />
							</DropdownMenu.Trigger>
							<DropdownMenu.Popup>
								{menuEntries(row.original).map((entry) => (
									<DropdownMenu.Item key={entry.label} onClick={entry.action}>
										{entry.label}
									</DropdownMenu.Item>
								))}
							</DropdownMenu.Popup>
						</DropdownMenu.Root>
					),
				}),
				columnHelper.accessor("oAuthClientName", {
					id: "oAuthClientName",
					header: messages["admin.oAuthClient.name"],
				}),
				columnHelper.accessor((row) => grantTypeLabel(row.authorizationGrantTypes), {
					id: "grantType",
					header: messages["oAuthClient.oAuthGrantType"],
				}),
				columnHelper.accessor("oAuthClientIdIssuedAt", {
					id: "oAuthClientIdIssuedAt",
					header: messages["admin.oAuthClient.created"],
					cell: ({ getValue }) =>
					formatDateTime(parseInstant(getValue())?.toZonedDateTimeISO(Temporal.Now.timeZoneId())),
				}),
			]),
		[menuEntries],
	);

	const table = useDataTable<OAuthClient>({
		data: oAuthClients ?? EMPTY,
		columns,
		getRowId: (oAuthClient) => String(oAuthClient.id),
		enableClientPagination: true,
		initialState: {
			sorting: [{ id: "oAuthClientName", desc: false }],
			pagination: { pageIndex: 0, pageSize: 20 },
		},
	});

	const handleAdd = () => {
		if (!apiFeatureAllowed) {
			setFeatureAlertOpen(true);
			return;
		}
		createDialog.show();
	};

	const handleCreated = (oAuthClient: OAuthClient) => {
		createDialog.close();
		reload();
		openDetails(oAuthClient, "view");
	};

	const startEdit = () => {
		const current = detailsDialog.value;
		if (current != null) {
			detailsDialog.update({ ...current, mode: "edit" });
		}
	};

	/** Editing ended: back to the view step only if the user came through it, otherwise close to the table. */
	const finishEdit = (saved?: OAuthClient) => {
		if (saved != null) {
			reload();
		}
		const current = detailsDialog.value;
		if (current == null || current.openedIn === "edit") {
			/* close, never drop the value — the content has to stay mounted for the exit transition */
			detailsDialog.close();
			return;
		}
		/* update, not show: the dialog stays open, so `key` must not advance */
		detailsDialog.update({ ...current, mode: "view", oAuthClient: saved ?? current.oAuthClient });
	};

	const confirmDelete = () => {
		if (deleting == null) {
			return;
		}
		const deleted = deleting;
		/* decided here rather than in the callback, where the dialog state would be a render behind */
		const closeDetails = detailsDialog.value?.oAuthClient.id === deleted.id;
		api("/admin/deleteOAuthClient.json")
			.post({ id: deleted.id })
			.done(() => {
				setDeleting(null);
				if (closeDetails) {
					detailsDialog.close();
				}
				reload();
			});
	};

	return (
		<div className="panel panel-flat">
			<div className="panel-heading">
				<h6 className="panel-title">{messages["oAuthClient.title"]}</h6>
				<samp className="text-muted">{REST_API_ENDPOINT}</samp>
				<div className="heading-elements">
					<div className="heading-btn">
						<Button variant="outline" onClick={handleAdd}>
							<FA6Icon icon="plus" variant="light" className="position-left" />
							{messages["oAuthClient.button.add"]}
						</Button>
					</div>
				</div>
			</div>

			<ContextMenu.Root onOpenChange={(open) => !open && setContextRow(null)}>
				<ContextMenu.Trigger>
					<DataTable.Root table={table} loading={loading || oAuthClients == null}>
						<DataTable.Content<OAuthClient>
							striped
							hover
							rowProps={(row) => ({
								onContextMenu: () => setContextRow(row.original),
							})}
							onRowDoubleClick={(oAuthClient) => openDetails(oAuthClient, "view")}
						/>
						<DataTable.Pagination />
					</DataTable.Root>
				</ContextMenu.Trigger>
				{contextRow != null && (
					<ContextMenu.Popup>
						{menuEntries(contextRow).map((entry) => (
							<ContextMenu.Item key={entry.label} onClick={entry.action}>
								{entry.label}
							</ContextMenu.Item>
						))}
					</ContextMenu.Popup>
				)}
			</ContextMenu.Root>

			<div className="panel-body">
				<Button
					variant="info"
					render={<a href={messages["oauth.info.link"]} target="_blank" rel="noreferrer" />}
				>
					<FA6Icon icon="circle-info" variant="regular" fixedWidth className="mr-5" />
					{messages["dialog.learnMore"]}
				</Button>
				<Button
					variant="secondary"
					className="ml-5"
					render={<a href={messages["oauth.developer.link"]} target="_blank" rel="noreferrer" />}
				>
					{messages["oauth.developer.title"]}
					<FA6Icon icon="arrow-up-right-from-square" variant="regular" fixedWidth className="ml-5" />
				</Button>
			</div>

			{/* Roots stay mounted for the closed -> open edge the fade transitions need; `key` remounts
			    the content on every open, and Base UI unmounts the popup after the exit transition */}
			<Modal.Root open={createDialog.open} onOpenChange={createDialog.onOpenChange}>
				<OAuthClientCreateDialog key={createDialog.key} onCreated={handleCreated} />
			</Modal.Root>

			<Modal.Root open={detailsDialog.open} onOpenChange={detailsDialog.onOpenChange}>
				{detailsDialog.value != null && (
					<OAuthClientDetailsDialog
						key={detailsDialog.key}
						oAuthClient={detailsDialog.value.oAuthClient}
						mode={detailsDialog.value.mode}
						apiFeatureAllowed={apiFeatureAllowed}
						oAuthSettings={oAuthSettings}
						onEditRequest={startEdit}
						onEditFinished={finishEdit}
						onDelete={setDeleting}
					/>
				)}
			</Modal.Root>

			<AlertDialog.Root open={featureAlertOpen} onOpenChange={setFeatureAlertOpen}>
				<AlertDialog.Content variation="warning" size="md" showCloseIcon backdropClose escapeClose>
					<AlertDialog.Header>
						<AlertDialog.Title>{messages["dialog.warning.title"]}</AlertDialog.Title>
					</AlertDialog.Header>
					<AlertDialog.Body>
						<p>{messages["oAuthClient.feature.notAvailable"]}</p>
					</AlertDialog.Body>
					<AlertDialog.Footer>
						<AlertDialog.Close render={<Button type="button" variant="secondary" />}>
							{messages["dialog.close"]}
						</AlertDialog.Close>
					</AlertDialog.Footer>
				</AlertDialog.Content>
			</AlertDialog.Root>

			<AlertDialog.Root open={deleting != null} onOpenChange={(open) => !open && setDeleting(null)}>
				<AlertDialog.Content variation="danger" size="md" showCloseIcon backdropClose escapeClose>
					<AlertDialog.Header>
						<AlertDialog.Title>{messages["dialog.warning.title"]}</AlertDialog.Title>
					</AlertDialog.Header>
					<AlertDialog.Body>
						<p>{messages.withArguments("dialog.confirm.delete.oAuthClient", deleting?.oAuthClientName ?? "")}</p>
					</AlertDialog.Body>
					<AlertDialog.Footer>
						<AlertDialog.Close render={<Button type="button" variant="secondary" />}>
							{messages["dialog.cancel"]}
						</AlertDialog.Close>
						<Button variant="destructive" onClick={confirmDelete}>
							{messages["confirmation.delete"]}
						</Button>
					</AlertDialog.Footer>
				</AlertDialog.Content>
			</AlertDialog.Root>
		</div>
	);
}

export function mountApiCredentialsPanelIsland(
	mountId: string,
	props: ApiCredentialsPanelProps,
): ReactIsland<ApiCredentialsPanelProps> | undefined {
	const mount = document.getElementById(mountId);
	if (!mount) {
		console.warn(`ApiCredentialsPanel mount point #${mountId} not found`);
		return undefined;
	}

	return new ReactIsland<ApiCredentialsPanelProps>(ApiCredentialsPanel, props, mount);
}
